import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import PageContainer from '../../PageContainer';
import PartnerPlanCard from './PartnerPlanCard';
import PartnerSubscriptionPayments from './PartnerSubscriptionPayments';
import { usePartnerPlansQuery } from '../../../api/use-partner-plans-query';
import { usePartnerPlansCheckoutMutation } from '../../../api/use-partner-plans-checkout-mutation';
import { partnerPlanPaymentsQueryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../contexts/AuthContext';

const VIEW_SUBSCRIPTION = 'subscription';
const VIEW_PAYMENTS = 'payments';

const PartnerManageSubscription = () => {
  const queryClient = useQueryClient();
  const { partnerProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [view, setView] = useState(VIEW_SUBSCRIPTION);

  const { data, isLoading, isError, error, refetch } = usePartnerPlansQuery();
  const checkoutMutation = usePartnerPlansCheckoutMutation();

  const plans = useMemo(() => data?.subscriptionPlans ?? [], [data?.subscriptionPlans]);
  const feePlan = plans[0] ?? null;
  const currentPlan = data?.currentPlan ?? partnerProfile?.currentPlan ?? null;
  const isCurrent = Boolean(currentPlan);
  const expiresAtLabel = isCurrent ? (currentPlan?.expiresAtLabel ?? null) : null;
  const showingPayments = view === VIEW_PAYMENTS;

  useEffect(() => {
    const checkoutResult = searchParams.get('checkout');
    if (!checkoutResult) return;

    if (checkoutResult === 'success') {
      toast.success('Subscription updated successfully');
      refetch();
      queryClient.invalidateQueries({ queryKey: partnerPlanPaymentsQueryKeys.all });
    } else if (checkoutResult === 'cancel') {
      toast('Checkout canceled');
    } else if (checkoutResult === 'failure') {
      toast.error('Checkout failed. Please try again.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refetch, queryClient]);

  const handleCheckout = async () => {
    if (!feePlan || checkoutMutation.isPending) return;

    setCheckoutStarted(true);
    try {
      const result = await checkoutMutation.mutateAsync({});
      window.location.assign(result.checkoutUrl);
    } catch (checkoutError) {
      toast.error(checkoutError?.message || 'Failed to start checkout');
      setCheckoutStarted(false);
    }
  };

  return (
    <PageContainer title="Manage Subscription">
      <Box
        sx={{
          maxWidth: showingPayments ? 960 : 720,
          width: '100%',
          minWidth: 0,
        }}
      >
        {showingPayments ? (
          <PartnerSubscriptionPayments onBack={() => setView(VIEW_SUBSCRIPTION)} />
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.375rem' },
                    color: 'text.primary',
                    letterSpacing: '-0.01em',
                    mb: 0.75,
                    lineHeight: 1.3,
                  }}
                >
                  Partner Portal fee
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: 520,
                    lineHeight: 1.55,
                  }}
                >
                  One monthly fee for your organization. Payment unlocks portal access for the
                  billing period.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                color="primary"
                startIcon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={() => setView(VIEW_PAYMENTS)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  px: 1.75,
                  py: 0.875,
                  whiteSpace: 'nowrap',
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                Payment history
              </Button>
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress color="primary" size={28} />
              </Box>
            ) : null}

            {isError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
                sx={{ borderRadius: '12px' }}
              >
                {error?.message || 'Failed to load subscription fee'}
              </Alert>
            ) : null}

            {!isLoading && !isError ? (
              !feePlan ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={6}>
                  No portal fee is available yet.
                </Typography>
              ) : (
                <PartnerPlanCard
                  plan={feePlan}
                  isCurrent={isCurrent}
                  expiresAtLabel={expiresAtLabel}
                  isBusy={checkoutMutation.isPending || checkoutStarted}
                  onSelect={handleCheckout}
                />
              )
            ) : null}
          </>
        )}
      </Box>
    </PageContainer>
  );
};

export default PartnerManageSubscription;
